// ─── TCPA reply-window correction banner ─────────────────────────────────────
// Shown on the campaign screen when a tenant's SAVED sending window falls outside
// federal quiet hours (8:00 AM – 9:00 PM). The server already refuses to send
// outside that window (clampToTcpaHours in _shared/timezone.ts), so nothing is at
// risk by the time this renders — but until it did, the settings form offered
// 6:00 AM and 10:00 PM and two live tenants picked exactly that. Their accounts
// still hold those values, and the number they'd read back is not what happens.
//
// Why the campaign screen and not settings: nobody returns to a settings page they
// already filled in. This is where they work, so this is where they get told.
//
// Three things this copy must always carry:
//   1. WHAT changed and to what — the exact hours now in effect, not vague wording.
//   2. Nothing was LOST — messages queue to the next legal opening, they don't drop.
//   3. WHY it's not optional — it's federal law with per-message penalties, not a
//      SurFox preference they can appeal.
//
// Self-hiding: reads live settings, so the moment they save a compliant window it
// disappears. No dismiss button on purpose — a dismissed banner about a live legal
// exposure is worse than no banner, and clearing it is one dropdown away.
//
// Demo tenants are exempt. is_demo accounts are sales-surface and internal-test
// accounts whose hours get set wide on purpose; there is no real customer to warn
// and the notice is noise on a screen someone is being shown. This hides the
// NOTICE only — the send-time clamp in _shared/timezone.ts still applies to them,
// so a demo account is no more able to text at 6 AM than anyone else.

import React, { useState, useEffect } from 'react';
import { ShieldAlert, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import supabase from '../lib/supabaseClient';
import { useAuth } from '../context/AuthContext';
import {
  isOutOfTcpaBounds,
  clampToTcpaHours,
  formatHour,
  TCPA_WINDOW_LABEL,
} from '../lib/tcpaHours';

export default function TcpaHoursBanner() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [issues, setIssues] = useState([]);

  useEffect(() => {
    const tenantId = user?.tenant_id;
    if (!tenantId) return;

    let cancelled = false;
    (async () => {
      // Demo accounts never see this notice. Checked before the settings read so a
      // demo tenant costs one query and never flashes the banner.
      const { data: tenantRow, error: tenantError } = await supabase
        .from('tenants')
        .select('is_demo')
        .eq('id', tenantId)
        .single();
      if (cancelled) return;
      // Fail open: if we cannot tell, treat the account as real and warn it. A
      // missed warning on a live tenant is a legal exposure; a stray warning on a
      // demo is cosmetic.
      if (!tenantError && tenantRow?.is_demo === true) return;

      const { data, error } = await supabase
        .from('platform_settings')
        .select('key, value')
        .eq('tenant_id', tenantId)
        .in('key', [
          'officeOpenHour', 'officeCloseHour',
          'replyOpenHour', 'replyCloseHour',
        ]);
      if (cancelled || error || !data) return;

      const s = {};
      data.forEach((row) => { s[row.key] = row.value; });

      // Reply tier inherits per-field from the office tier when blank, matching
      // loadTenantHours — so check the EFFECTIVE reply window, not the raw keys.
      const replyOpen = s.replyOpenHour ?? s.officeOpenHour;
      const replyClose = s.replyCloseHour ?? s.officeCloseHour;

      const found = [];
      if (isOutOfTcpaBounds(s.officeOpenHour, s.officeCloseHour)) {
        found.push({
          label: 'Initial message hours',
          from: [s.officeOpenHour, s.officeCloseHour],
          to: clampToTcpaHours(s.officeOpenHour, s.officeCloseHour),
        });
      }
      if (isOutOfTcpaBounds(replyOpen, replyClose)) {
        found.push({
          label: 'Reply hours',
          from: [replyOpen, replyClose],
          to: clampToTcpaHours(replyOpen, replyClose),
        });
      }
      setIssues(found);
    })();

    return () => { cancelled = true; };
  }, [user?.tenant_id]);

  if (issues.length === 0) return null;

  return (
    <div className="rounded-xl border border-amber-300 bg-amber-50 p-4">
      <div className="flex gap-3">
        <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-amber-900">
            We adjusted your sending hours to stay legal
          </h3>

          <p className="mt-1.5 text-sm leading-relaxed text-amber-900">
            Federal law (TCPA) only allows business texts between {TCPA_WINDOW_LABEL} and your
            account was set wider than that. We are not sending outside the legal window, so
            there is nothing for you to fix urgently. Your leads are safe: anything that lands
            outside these hours waits and goes out at the next legal opening rather than being
            dropped.
          </p>

          <div className="mt-3 space-y-1.5">
            {issues.map((it) => (
              <div key={it.label} className="flex flex-wrap items-center gap-2 text-sm">
                <span className="font-medium text-amber-900">{it.label}:</span>
                <span className="text-amber-700 line-through">
                  {formatHour(it.from[0])} – {formatHour(it.from[1])}
                </span>
                <ArrowRight className="h-3.5 w-3.5 text-amber-500" />
                <span className="rounded-md bg-amber-200/70 px-2 py-0.5 font-semibold text-amber-900">
                  {formatHour(it.to.openHour)} – {formatHour(it.to.closeHour)}
                </span>
                <span className="text-amber-700">now in effect</span>
              </div>
            ))}
          </div>

          <button
            type="button"
            onClick={() => navigate('/settings?tab=company-profile')}
            className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-amber-600 px-3 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-amber-700"
          >
            Update my hours
            <ArrowRight className="h-3.5 w-3.5" />
          </button>
          <p className="mt-2 text-xs text-amber-700">
            Saving an allowed time removes this notice.
          </p>
        </div>
      </div>
    </div>
  );
}
